/**
 * Widget Parser - Parse widget embeds from text
 *
 * Supports two syntax formats:
 *
 * 1. HTML Attributes (NEW - preferred):
 *    - Realtime: <widget type="stock_info" mode="realtime" params='{"tickers":["HPG"]}'></widget>
 *    - Static:   <widget artifact_id="widget_abc123"></widget>
 *
 * 2. JSON-inside-tags (LEGACY - backward compatible):
 *    <widget>{"type":"stock_info","params":{...}}</widget>
 *    <widget>{"artifact_id":"widget_abc123"}</widget>
 *
 * Two modes:
 * - Realtime: type + mode + params → Client calls API
 * - Static: artifact_id only (no type/mode needed) → Fetch from DB
 */

// ============================================================================
// Patterns
// ============================================================================

// HTML Attributes format: <widget type="..." mode="..." params='...'></widget> or <widget ... />
const WIDGET_ATTRS_PATTERN = /<widget\s+([^>]*?)(?:\/>|><\/widget>)/gi;

// Legacy JSON format: <widget>json</widget>
const WIDGET_JSON_PATTERN = /<widget>([\s\S]*?)<\/widget>/g;

// Combined pattern for detection (matches both formats)
const WIDGET_COMBINED_PATTERN = /<widget(?:\s+[^>]*?(?:\/>|><\/widget>)|>([\s\S]*?)<\/widget>)/gi;

// Incomplete widget detection for streaming
const INCOMPLETE_WIDGET_PATTERN = /<widget(?:\s+[^>]*)?$/;

// Attribute parser: key="value" or key='value'
const ATTR_PATTERN = /(\w+)=["']([^"']*?)["']/g;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract JSON from params attribute with bracket balancing
 * Handles cases where JSON contains quotes that break simple regex
 * Also handles escaped quotes (\") from LLM output
 */
function extractParamsJson(attrString) {
    // Try single quotes first (preferred, cleaner)
    const singleQuoteMatch = attrString.match(/params='([^']*)'/);
    if (singleQuoteMatch) {
        let json = singleQuoteMatch[1];
        // Unescape \" to " (LLM sometimes outputs escaped quotes)
        json = json.replace(/\\"/g, '"');
        return json;
    }

    // For double quotes, use bracket balancing
    const paramsStart = attrString.indexOf('params="');
    if (paramsStart === -1) return null;

    const jsonStart = paramsStart + 8; // 'params="'.length
    let depth = 0;
    let inString = false;
    let escape = false;

    for (let i = jsonStart; i < attrString.length; i++) {
        const char = attrString[i];

        if (escape) {
            escape = false;
            continue;
        }

        if (char === '\\') {
            escape = true;
            continue;
        }

        if (char === '"' && depth === 0 && !inString) {
            // End of params attribute
            let json = attrString.slice(jsonStart, i);
            // Unescape \" to " (LLM sometimes outputs escaped quotes)
            json = json.replace(/\\"/g, '"');
            return json;
        }

        if (char === '"') {
            inString = !inString;
        } else if (!inString) {
            if (char === '[' || char === '{') depth++;
            if (char === ']' || char === '}') depth--;
        }
    }

    return null;
}

/**
 * Parse HTML attributes from attribute string
 * @param {string} attrString - Attribute string like 'type="stock_info" mode="realtime" params=\'{"x":1}\''
 * @returns {Object} Parsed attributes
 */
function parseWidgetAttributes(attrString) {
    const attrs = {};

    // Special handling for params attribute (contains JSON with quotes)
    const paramsJson = extractParamsJson(attrString);
    if (paramsJson) {
        try {
            attrs.params = JSON.parse(paramsJson);
        } catch (e) {
            console.warn('Failed to parse widget params JSON:', paramsJson, e);
            attrs.params = paramsJson;
        }
    }

    // Parse other simple attributes (type, mode, artifact_id)
    const simpleAttrs = ['type', 'mode', 'artifact_id'];
    for (const attr of simpleAttrs) {
        const match = attrString.match(new RegExp(`${attr}=["']([^"']+)["']`));
        if (match) {
            attrs[attr] = match[1];
        }
    }

    return attrs;
}

/**
 * Try to extract widget type from partial content (for streaming)
 * Works with both formats
 * @param {string} partialContent - Partial widget content
 * @returns {string} Detected type or 'unknown'
 */
function extractWidgetTypeFromPartial(partialContent) {
    // Try HTML attributes format first: type="something"
    const typeAttrMatch = partialContent.match(/type=["']([^"']+)["']/);
    if (typeAttrMatch) {
        return typeAttrMatch[1];
    }

    // Try JSON format: "type":"something"
    const typeJsonMatch = partialContent.match(/"type"\s*:\s*"([^"]+)"/);
    if (typeJsonMatch) {
        return typeJsonMatch[1];
    }

    return 'unknown';
}

/**
 * Check if a widget tag is HTML attributes format
 * @param {string} widgetTag - Full widget tag
 * @returns {boolean}
 */
function isAttributesFormat(widgetTag) {
    // Attributes format has attributes after <widget and ends with /> or ></widget>
    return /^<widget\s+[^>]*(?:\/>|><\/widget>)$/i.test(widgetTag);
}

/**
 * Parse a single widget tag and return config
 * @param {string} widgetTag - Full widget tag
 * @returns {Object|null} Widget config or null if invalid
 */
function parseWidgetTag(widgetTag) {
    // Try HTML attributes format first
    const attrsMatch = widgetTag.match(/<widget\s+([^>]*?)(?:\/>|><\/widget>)/i);
    if (attrsMatch) {
        const attrs = parseWidgetAttributes(attrsMatch[1]);

        // Static mode: artifact_id is a separate attribute (no type/mode needed)
        if (attrs.artifact_id) {
            return {
                artifact_id: attrs.artifact_id,
                mode: 'static'
            };
        }

        // Realtime mode: needs type, mode, params
        if (attrs.type) {
            return {
                type: attrs.type,
                mode: attrs.mode || 'realtime',
                params: attrs.params  // Keep params as-is (array or object)
            };
        }
    }

    // Try JSON format (legacy)
    const jsonMatch = widgetTag.match(/<widget>([\s\S]*?)<\/widget>/i);
    if (jsonMatch) {
        try {
            const config = JSON.parse(jsonMatch[1]);
            return config;
        } catch (e) {
            console.warn('Failed to parse widget JSON:', jsonMatch[1], e);
        }
    }

    return null;
}

// ============================================================================
// Main Parser Functions
// ============================================================================

/**
 * Parse widgets from text content
 *
 * @param {string} text - Text content with widget embeds
 * @param {boolean} isStreaming - Whether content is still streaming (default: false)
 * @returns {Array} Array of parts: {type: 'text', content} or {type: 'widget', config} or {type: 'widget_loading', widgetType}
 *
 * @example
 * // Realtime widget (HTML Attributes)
 * parseWidgets("Hello <widget type=\"stock_info\" mode=\"realtime\" params='{\"tickers\":[\"HPG\"]}'></widget> World")
 * // Returns:
 * // [
 * //   { type: 'text', content: 'Hello ' },
 * //   { type: 'widget', config: { type: 'stock_info', mode: 'realtime', tickers: ['HPG'] } },
 * //   { type: 'text', content: ' World' }
 * // ]
 *
 * @example
 * // Static widget (artifact_id only, no type/mode needed)
 * parseWidgets("Chart: <widget artifact_id=\"widget_abc123\"></widget>")
 * // Returns:
 * // [
 * //   { type: 'text', content: 'Chart: ' },
 * //   { type: 'widget', config: { artifact_id: 'widget_abc123', mode: 'static' } }
 * // ]
 *
 * @example
 * // JSON format (legacy)
 * parseWidgets("Hello <widget>{\"type\":\"stock_info\"}</widget> World")
 * // Returns:
 * // [
 * //   { type: 'text', content: 'Hello ' },
 * //   { type: 'widget', config: { type: 'stock_info' } },
 * //   { type: 'text', content: ' World' }
 * // ]
 */
export function parseWidgets(text, isStreaming = false) {
    if (!text || typeof text !== 'string') {
        return [{ type: 'text', content: text || '' }];
    }

    const parts = [];
    let lastIndex = 0;

    // Combined pattern to match both formats
    // Group 1: Attributes content (for attrs format)
    // Group 2: JSON content (for json format)
    const combinedPattern = /<widget(?:\s+([^>]*?)(?:\/>|><\/widget>)|(>([\s\S]*?)<\/widget>))/gi;
    combinedPattern.lastIndex = 0;

    let match;
    while ((match = combinedPattern.exec(text)) !== null) {
        const fullMatch = match[0];
        const attrsContent = match[1]; // HTML attributes content
        const jsonContent = match[3]; // JSON content
        const offset = match.index;

        // Add text before widget
        if (offset > lastIndex) {
            parts.push({
                type: 'text',
                content: text.slice(lastIndex, offset)
            });
        }

        // Parse widget config based on format
        let config = null;

        if (attrsContent !== undefined) {
            // HTML Attributes format
            const attrs = parseWidgetAttributes(attrsContent);

            // Static mode: artifact_id is a separate attribute
            if (attrs.artifact_id) {
                config = {
                    artifact_id: attrs.artifact_id,
                    mode: 'static'
                };
            }
            // Realtime mode: needs type
            else if (attrs.type) {
                config = {
                    type: attrs.type,
                    mode: attrs.mode || 'realtime',
                    params: attrs.params  // Keep params as-is (array or object)
                };
            }
        } else if (jsonContent !== undefined) {
            // JSON format (legacy)
            try {
                config = JSON.parse(jsonContent);
            } catch (e) {
                console.warn('Failed to parse widget JSON:', jsonContent, e);
            }
        }

        if (config) {
            parts.push({
                type: 'widget',
                config
            });
        } else {
            // If parsing failed, keep as text
            parts.push({
                type: 'text',
                content: fullMatch
            });
        }

        lastIndex = offset + fullMatch.length;
    }

    // Check for incomplete widget tag at the end (during streaming)
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
        // Check if there's an incomplete widget tag
        const incompleteMatch = remainingText.match(INCOMPLETE_WIDGET_PATTERN);

        if (incompleteMatch && isStreaming) {
            // Get text before the incomplete widget
            const incompleteStart = remainingText.indexOf('<widget');
            if (incompleteStart > 0) {
                parts.push({
                    type: 'text',
                    content: remainingText.slice(0, incompleteStart)
                });
            }

            // Extract partial content and try to determine widget type
            const partialContent = remainingText.slice(incompleteStart + 7); // 7 = '<widget'.length
            const widgetType = extractWidgetTypeFromPartial(partialContent);

            parts.push({
                type: 'widget_loading',
                widgetType: widgetType
            });
        } else {
            // No incomplete widget, add as regular text
            parts.push({
                type: 'text',
                content: remainingText
            });
        }
    }

    // If no widgets found, return single text part
    if (parts.length === 0) {
        parts.push({ type: 'text', content: text });
    }

    return parts;
}

/**
 * Check if widget config is realtime mode (has type or api field)
 * Realtime widgets can have:
 * - type + params (client resolves API from type)
 * - api + params (legacy, explicit API)
 * @param {Object} config - Widget config
 * @returns {boolean}
 */
export function isRealtimeWidget(config) {
    if (!config || config.artifact_id) return false;
    // Has explicit api, or has type (client will resolve API from type)
    // Also check mode field
    if (config.mode === 'static') return false;
    return !!(config.api || config.type);
}

/**
 * Check if widget config is static mode (has artifact_id)
 * @param {Object} config - Widget config
 * @returns {boolean}
 */
export function isStaticWidget(config) {
    return !!(config && (config.artifact_id || config.mode === 'static'));
}

/**
 * Get widget mode
 * @param {Object} config - Widget config
 * @returns {'realtime' | 'static' | 'unknown'}
 */
export function getWidgetMode(config) {
    if (isStaticWidget(config)) return 'static';
    if (isRealtimeWidget(config)) return 'realtime';
    return 'unknown';
}

/**
 * Parse combined content with both artifacts and widgets
 *
 * Handles both patterns:
 * - Legacy: [artifact:type:id]
 * - New: <widget type="..." mode="..." params='...'></widget>
 * - JSON: <widget>json</widget>
 *
 * @param {string} text - Text content
 * @param {boolean} isStreaming - Whether content is still streaming (default: false)
 * @returns {Array} Array of parts
 */
export function parseContentWithWidgets(text, isStreaming = false) {
    if (!text || typeof text !== 'string') {
        return [{ type: 'text', content: text || '' }];
    }

    // First parse widgets (pass isStreaming to detect incomplete widgets)
    const widgetParts = parseWidgets(text, isStreaming);

    // Then parse artifacts and visualizations within text parts
    const ARTIFACT_PATTERN = /\[artifact:(\w+):([a-zA-Z0-9_]+)\]/g;
    const VISUALIZATION_PATTERN = /^visualization:(visual_[a-z0-9]+)$/gm;
    const COMBINED_EMBED_PATTERN = /(?:\[artifact:(\w+):([a-zA-Z0-9_]+)\]|^visualization:(visual_[a-z0-9]+)$)/gm;
    const finalParts = [];

    for (const part of widgetParts) {
        if (part.type === 'widget' || part.type === 'widget_loading') {
            finalParts.push(part);
            continue;
        }

        // Parse artifacts and visualizations in text part
        const textContent = part.content;
        let lastIndex = 0;
        COMBINED_EMBED_PATTERN.lastIndex = 0;

        let match;
        while ((match = COMBINED_EMBED_PATTERN.exec(textContent)) !== null) {
            const fullMatch = match[0];
            const artifactType = match[1];
            const artifactId = match[2];
            const visualizationId = match[3];
            const offset = match.index;

            if (offset > lastIndex) {
                finalParts.push({
                    type: 'text',
                    content: textContent.slice(lastIndex, offset)
                });
            }

            if (visualizationId) {
                finalParts.push({
                    type: 'visualization',
                    artifactId: visualizationId
                });
            } else {
                finalParts.push({
                    type: 'artifact',
                    artifactType: artifactType,
                    artifactId: artifactId
                });
            }

            lastIndex = offset + fullMatch.length;
        }

        // If no embeds found in this text part, keep original
        if (lastIndex === 0) {
            finalParts.push(part);
        } else if (lastIndex < textContent.length) {
            // Add remaining text after last embed
            finalParts.push({
                type: 'text',
                content: textContent.slice(lastIndex)
            });
        }
    }

    return finalParts.length > 0 ? finalParts : [{ type: 'text', content: text }];
}

/**
 * Format widget config as HTML attributes embed string (new format)
 * @param {Object} config - Widget config
 * @returns {string} Formatted embed string
 *
 * @example
 * // Realtime widget
 * formatWidgetEmbed({ type: 'stock_info', mode: 'realtime', tickers: ['HPG'] })
 * // Returns: '<widget type="stock_info" mode="realtime" params=\'{"tickers":["HPG"]}\'></widget>'
 *
 * @example
 * // Static widget (artifact_id only)
 * formatWidgetEmbed({ artifact_id: 'widget_abc123' })
 * // Returns: '<widget artifact_id="widget_abc123"></widget>'
 */
export function formatWidgetEmbed(config) {
    // Static mode: artifact_id only
    if (config.artifact_id) {
        return `<widget artifact_id="${config.artifact_id}"></widget>`;
    }

    // Realtime mode: type + mode + params
    const type = config.type || 'unknown';
    const mode = config.mode || 'realtime';

    // Extract params (everything except type and mode)
    const { type: _, mode: __, ...params } = config;

    const paramsJson = JSON.stringify(params);

    return `<widget type="${type}" mode="${mode}" params='${paramsJson}'></widget>`;
}

export default {
    parseWidgets,
    parseContentWithWidgets,
    isRealtimeWidget,
    isStaticWidget,
    getWidgetMode,
    formatWidgetEmbed,
    parseWidgetAttributes,
    parseWidgetTag
};
